import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from './store';
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageFromUrl } from '../utilities/languageUrls';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Hook to automatically update language when URL changes
 */
export function useLanguageFromUrl() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const lastLanguageRef = useRef<string>(i18n.language);

  const updateLanguage = useCallback((newLanguage: string) => {
    if (lastLanguageRef.current !== newLanguage) {
      lastLanguageRef.current = newLanguage;
      i18n.changeLanguage(newLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const currentLanguage = getLanguageFromUrl(location.pathname);
    updateLanguage(currentLanguage);
  }, [location.pathname, updateLanguage]);

  // Handle initial load
  useEffect(() => {
    const currentLanguage = getLanguageFromUrl(location.pathname);
    if (i18n.language !== currentLanguage) {
      updateLanguage(currentLanguage);
    }
  }, [i18n.language, updateLanguage, location.pathname]);
}
